import { memo, type PointerEvent as ReactPointerEvent } from 'react';
import type { Archive } from '../../api/archives';
import { clipEnd } from '../../format/helpers';
import type { AnyClip, Track } from '../../format/types';
import { useTimeline } from '../../state/timeline';
import { useTimelineCtx } from './context';
import { ClipBlock } from './ClipBlock';

interface LaneProps {
  track: Track;
  archives: Archive[];
  /** visible px window incl. overscan — clips wholly outside it are not mounted */
  cullRange: [number, number];
}

/** Memoized: a mutation replaces only the touched track's identity, so the
 * other lanes skip entirely; within a re-rendered lane the memoized ClipBlocks
 * skip for every clip whose identity survived (see crdt/share.ts). */
export const TrackLane = memo(function TrackLane({ track, archives, cullRange }: LaneProps) {
  const { pps, controls, laneRefs, dropTrackId } = useTimelineCtx();
  const isDropTarget = track.id === dropTrackId;
  const visible = (track.clips as AnyClip[]).filter(
    (clip) => clip.start * pps < cullRange[1] && clipEnd(clip) * pps > cullRange[0],
  );

  function onLaneClick(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    controls.seek((e.clientX - rect.left) / pps);
    useTimeline.getState().clearSelection();
  }

  return (
    <div
      className={`tl-lane tl-lane-${track.kind} ${isDropTarget ? 'drop-target' : ''}`}
      ref={(el) => {
        if (el) laneRefs.current.set(track.id, el);
        else laneRefs.current.delete(track.id);
      }}
      onPointerDown={onLaneClick}
    >
      {visible.map((clip) => (
        <ClipBlock
          key={clip.id}
          clip={clip}
          trackId={track.id}
          trackKind={track.kind}
          archives={archives}
        />
      ))}
      {track.kind === 'video' && <TransitionBadges track={track} />}
    </div>
  );
});

function TransitionBadges({ track }: { track: Extract<Track, { kind: 'video' }> }) {
  const { pps } = useTimelineCtx();
  const badges: JSX.Element[] = [];
  for (let i = 1; i < track.clips.length; i++) {
    const b = track.clips[i];
    const a = track.clips[i - 1];
    const aEnd = clipEnd(a);
    if (b.transitionIn && b.start < aEnd) {
      badges.push(
        <button
          key={b.id}
          className="tl-transition-badge"
          style={{ left: ((b.start + aEnd) / 2) * pps }}
          title={`${b.transitionIn.type} (${b.transitionIn.duration.toFixed(2)}s) — edit in inspector`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            useTimeline.getState().select(b.id);
          }}
        >
          ⧓
        </button>,
      );
    }
  }
  return <>{badges}</>;
}
